import { FunctionComponent, MutableRefObject, useEffect, useRef, useState } from "react";
import { gql, useQuery, useMutation } from "@shared/utils";
import { PRODUCT_CONFIGURATION_VIEW_INNER } from "../productOptionQuerys";
import {
  ProductOptionProvider,
  useProductOptionContext,
} from "./ProductOptionContext";
import {
  ExtendedProductConfigurationVariablesProvider,
  useProductConfigurationVariables,
} from "../../providers/ProductConfigurationVariables";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  useTheme,
} from "@mui/material";

import { RemoveCategory } from "../../pages/dialogs/RemoveCategory";
import { IcIcon } from "@shared/components";
import { POItemBox } from "./POItemBox";
import { OptionsList, TValidator } from "@shared/types";
import { groupByCategory } from "../../utils/group";
import { OptionsTab } from "./OptionsTab/OptionsTab";
import { FeaturesTab } from "./Features/FeaturesTab";

export type OptionListItem_SubCategory_Option = {
  name: string;
  _id: string;
  sortOrder?: number;
  validators?: TValidator[];
  AllowList: Array<{ subcategory: string; options: string[] }>;
} & Pick<OptionsList, "variableType" | "number" | "text" | "unit">;

export type OptionListItem = {
  options: string[];
  SubCategory?: {
    name: string;
    _id: string;
    Category: {
      name: string;
      _id: string;
    };
    maxOptions: number;
    sortOrder?: number;
    Options: Array<OptionListItem_SubCategory_Option>;
  };
} & Pick<OptionsList, "variableType" | "number" | "text" | "unit">;

export type ProductFeature = {
  _id: string;
  name: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
  categoryIds: string[];
  incompatibleCategoryIds: string[];
  optionalCategoryIds: string[];
};

export type ProductOptionsData = {
  _id: string;
  product: string;
  OptionsList: OptionListItem[];
  ProductFeatures: ProductFeature[];
  productFeatures: string[];
  Categories: Array<{
    _id: string;
    name: string;
    parentProductCategory?: string;
    useParentValues?: boolean;
  }>;
  ParentProductConfiguration?: {
    OptionsList: OptionListItem[];
  };
};

export type CategoryData = {
  SubCategorys: Array<{
    name: string;
    _id: string;
    Options: Array<{
      name: string;
      _id: string;
      AllowList: Array<{ subcategory: string; options: string[] }>;
      validators?: TValidator[];
    }>;
  }>;
  Options: Array<{
    name: string;
    _id: string;
    requiredCategorys: string[];
    AllowList: Array<{ subcategory: string; options: string[] }>;
  }>;
  requiredCategorys: string[];
  incompatibleCategorys: string[];
  useParentValues?: boolean;
  parentProductCategory?: string;
  name: string;
  _id: string;
};

const REVERT_REPLACE_OPTIONS = gql`
  mutation RevertReplaceOptionsListProduct($id: String!, $value: [String!]!, $subCategoryId: String!) {
    replaceOptionsListProductConfiguration(input: { id: $id, subCategoryId: $subCategoryId, optionIds: $value }) {
      ${PRODUCT_CONFIGURATION_VIEW_INNER}
    }
  }
`;
const REVERT_SET_VARIABLE_TYPE = gql`
  mutation RevertSetVariableType($id: String!, $value: String!, $subCategoryId: String!) {
    updateOptionsListProductConfiguration(input: { id: $id, subCategoryId: $subCategoryId, variableType: $value }) {
      ${PRODUCT_CONFIGURATION_VIEW_INNER}
    }
  }
`;
const REVERT_SET_NUMBER = gql`
  mutation RevertSetNumber($id: String!, $value: Float!, $subCategoryId: String!) {
    updateOptionsListProductConfiguration(input: { id: $id, subCategoryId: $subCategoryId, number: $value }) {
      ${PRODUCT_CONFIGURATION_VIEW_INNER}
    }
  }
`;
const REVERT_SET_TEXT = gql`
  mutation RevertSetText($id: String!, $value: String!, $subCategoryId: String!) {
    updateOptionsListProductConfiguration(input: { id: $id, subCategoryId: $subCategoryId, text: $value }) {
      ${PRODUCT_CONFIGURATION_VIEW_INNER}
    }
  }
`;
const REVERT_SET_UNIT = gql`
  mutation RevertSetUnit($id: String!, $value: String!, $subCategoryId: String!) {
    updateOptionsListProductConfiguration(input: { id: $id, subCategoryId: $subCategoryId, unit: $value }) {
      ${PRODUCT_CONFIGURATION_VIEW_INNER}
    }
  }
`;

const parentQuery = gql`
  query ProductConfiguration($id: ID!) {
    productConfiguration(id: $id) {
      parentProductConfigurationId
    }
  }
`;

// Define union types for ProductOptionCoreProps:
// Normal usage (e.g. when wrapped by <ProductOptionProvider> via <ProductOption>)
type NormalProps = {
  showAdd: boolean;
  setShowAdd: (b: boolean) => any;
  tab: string | null;
  setTab: (b: string | null) => any;
  readonly?: boolean;
  onOptionsChange?: () => void;
  revertOptionsRef?: MutableRefObject<(() => Promise<void>) | null>;
  takeoffId?: undefined;
  productConfiguration?: never;
  parentProductConfigurationId?: never;
};
// Fallback usage (e.g. direct usage with takeoffId)
type FallbackProps = {
  showAdd: boolean;
  setShowAdd: (b: boolean) => any;
  tab: string | null;
  setTab: (b: string | null) => any;
  readonly?: boolean;
  onOptionsChange?: () => void;
  revertOptionsRef?: MutableRefObject<(() => Promise<void>) | null>;
  takeoffId: string;
  productConfiguration: string;
  parentProductConfigurationId?: string;
};

export type ProductOptionCoreProps = NormalProps | FallbackProps;

export const ProductOption: FunctionComponent<{
  showAdd: boolean;
  setShowAdd: (b: boolean) => any;
}> = (props) => {
  const { activeConfigurationId, tab, setTab } =
    useProductConfigurationVariables();
  const { data } = useQuery<{
    productConfiguration?: { parentProductConfigurationId?: string };
  }>(parentQuery, {
    variables: { id: activeConfigurationId },
    skip: !activeConfigurationId,
  });
  return (
    <ProductOptionProvider
      productConfiguration={activeConfigurationId ?? ''}
      parentProductConfigurationId={
        data?.productConfiguration?.parentProductConfigurationId
      }
      takeoffId={undefined}
    >
      <ProductOptionCoreInner
        showAdd={props.showAdd}
        setShowAdd={props.setShowAdd}
        tab={tab ?? null}
        setTab={(b: string | null) => setTab(b ?? undefined)}
        readonly={false}
      />
    </ProductOptionProvider>
  );
};
export const ProductOptionCore: FunctionComponent<ProductOptionCoreProps> = (
  props
) => {
  return (
    <ProductOptionProvider
      productConfiguration={(props.productConfiguration as string) ?? ''}
      parentProductConfigurationId={props.parentProductConfigurationId}
      takeoffId={props.takeoffId}
    >
      <ProductOptionCoreInner
        showAdd={props.showAdd}
        setShowAdd={props.setShowAdd}
        tab={props.tab}
        setTab={props.setTab}
        readonly={props.readonly}
        onOptionsChange={props.onOptionsChange}
        revertOptionsRef={props.revertOptionsRef}
        productConfiguration={props.productConfiguration as string}
        takeoffId={props.takeoffId as string}
        parentProductConfigurationId={props.parentProductConfigurationId}
      />
    </ProductOptionProvider>
  );
};
export const ProductOptionCoreInner: FunctionComponent<
  ProductOptionCoreProps
> = (props) => {
  const {
    optionsTab,
    setOptionsTab,
    selectedCategories,
    activeLoadingCategory,
    setActiveLoadingCategory,
    productConfigurationQuery,
    parentProductConfigurationQuery,
    categoryQuery,
    addCategory,
    loading,
    catmap,
    parentcatmap,
    categorys,
    productConfigurationId,
    groupedOptions,
    parentGroupedOptions,
    parentCategoriesIds,
    shapeData,
    cancelAllOptionDebounces,
  } = useProductOptionContext();

  const [categoryToRemove, setCategoryToRemove] = useState<string | null>(null);

  // Change tracking: detect category additions/removals and option selection changes.
  // OptionSelect mutations return the full productConfiguration shape, so Apollo's
  // normalized cache updates groupedOptions automatically on any option change.
  const optionInitializedRef = useRef(false);
  const originalSignatureRef = useRef<string | null>(null);
  const optionSignatureRef = useRef("");
  const originalItemsRef = useRef<Record<string, { options: string[]; variableType: string; number: number; text: string; unit: string }>>({});
  const groupedOptionsRef = useRef(groupedOptions);

  const [replaceOptionsForRevert] = useMutation(REVERT_REPLACE_OPTIONS);
  const [setVariableTypeForRevert] = useMutation(REVERT_SET_VARIABLE_TYPE);
  const [setNumberForRevert] = useMutation(REVERT_SET_NUMBER);
  const [setTextForRevert] = useMutation(REVERT_SET_TEXT);
  const [setUnitForRevert] = useMutation(REVERT_SET_UNIT);
  groupedOptionsRef.current = groupedOptions;
  // Include BOTH groupedOptions AND parentGroupedOptions in the signature
  // to detect changes from both normal components and parent components (sub takeoff/sub product)
  optionSignatureRef.current = JSON.stringify({
    main: groupedOptions.map((g) => ({
      catId: g.category._id,
      items: g.items.map((item: any) => ({
        subCatId: item.SubCategory?._id,
        options: [...(item.options || [])].sort(),
        variableType: item.variableType,
        number: item.number,
        text: item.text,
        unit: item.unit,
      })),
    })),
    parent: parentGroupedOptions.map((g) => ({
      catId: g.category._id,
      items: g.items.map((item: any) => ({
        subCatId: item.SubCategory?._id,
        options: [...(item.options || [])].sort(),
        variableType: item.variableType,
        number: item.number,
        text: item.text,
        unit: item.unit,
      })),
    })),
  });

  // Reset when productConfiguration changes (new modal target)
  useEffect(() => {
    optionInitializedRef.current = false;
    originalSignatureRef.current = null;
    originalItemsRef.current = {};
  }, [productConfigurationId]);

  // Snapshot initial state after a short delay to let queries settle
  // Include both groupedOptions and parentGroupedOptions to capture initial state of all components
  useEffect(() => {
    if (optionInitializedRef.current || (!groupedOptions.length && !parentGroupedOptions.length)) return;
    const timer = setTimeout(() => {
      originalSignatureRef.current = optionSignatureRef.current;
      const map: Record<string, { options: string[]; variableType: string; number: number; text: string; unit: string }> = {};
      for (const g of groupedOptionsRef.current) {
        for (const item of g.items as any[]) {
          if (item.SubCategory?._id) {
            map[item.SubCategory._id] = {
              options: [...(item.options || [])].sort(),
              variableType: item.variableType || "none",
              number: item.number || 0,
              text: item.text || "",
              unit: item.unit || "",
            };
          }
        }
      }
      originalItemsRef.current = map;
      optionInitializedRef.current = true;
    }, 100);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedOptions, parentGroupedOptions]);

  // Fire onOptionsChange whenever groupedOptions OR parentGroupedOptions diverges from the snapshot
  // This ensures parent components (sub takeoff/sub product) also trigger close/sync validation
  useEffect(() => {
    if (!optionInitializedRef.current || originalSignatureRef.current === null) return;
    if (optionSignatureRef.current !== originalSignatureRef.current) {
      props.onOptionsChange?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedOptions, parentGroupedOptions]);

  // Keep revertOptionsRef current on every render so Cancel always has fresh closures
  if (props.revertOptionsRef) {
    props.revertOptionsRef.current = async () => {
      const original = originalItemsRef.current;
      // CRITICAL: Cancel all pending debounced option mutations BEFORE firing revert mutations.
      // Without this, a pending debounce could fire AFTER the revert, overwriting it.
      cancelAllOptionDebounces(original);
      for (const g of groupedOptionsRef.current) {
        for (const item of g.items as any[]) {
          const subCatId = item.SubCategory?._id;
          if (!subCatId || !original[subCatId]) continue;
          const orig = original[subCatId];
          const currentOptions = [...(item.options || [])].sort();
          const originalOptions = [...(orig.options || [])].sort();
          if (JSON.stringify(currentOptions) !== JSON.stringify(originalOptions)) {
            await replaceOptionsForRevert({ variables: { id: productConfigurationId, value: orig.options, subCategoryId: subCatId } });
          }
          if ((item.variableType || "none") !== (orig.variableType || "none")) {
            await setVariableTypeForRevert({ variables: { id: productConfigurationId, value: orig.variableType || "none", subCategoryId: subCatId } });
          }
          if ((item.number || 0) !== (orig.number || 0)) {
            await setNumberForRevert({ variables: { id: productConfigurationId, value: orig.number || 0, subCategoryId: subCatId } });
          }
          if ((item.text || "") !== (orig.text || "")) {
            await setTextForRevert({ variables: { id: productConfigurationId, value: orig.text || "", subCategoryId: subCatId } });
          }
          if ((item.unit || "") !== (orig.unit || "")) {
            await setUnitForRevert({ variables: { id: productConfigurationId, value: orig.unit || "", subCategoryId: subCatId } });
          }
        }
      }
    };
  }

  return (
    <ExtendedProductConfigurationVariablesProvider
      productConfiguration={productConfigurationId}
    >
      {groupedOptions.map(({ items, category }, index) => (
        <POItemBox
          key={`main-${category._id}-${category.name || ""}-${index}`}
          {...{
            items,
            category,
            catmap,
            productConfiguration: productConfigurationId,
            readonly: props.readonly,
            originalItems: originalItemsRef,
            onBeforeChange: props.onOptionsChange,
            setShowRemove: (categoryId: string | boolean) => {
              if (!categoryId) {
                props.setTab("");
                return;
              }
              if (typeof categoryId === "string") {
                setCategoryToRemove(categoryId);
              }
            },
          }}
        />
      ))}
      {parentGroupedOptions.map(({ items, category }, index) => (
        <POItemBox
          key={`parent-${category._id}-${category.name || ""}-${index}`}
          {...{
            items,
            category,
            catmap: parentcatmap,
            productConfiguration: productConfigurationId,
            readonly: true,
            setShowRemove: (categoryId: string | boolean) => {
              if (!categoryId) {
                props.setTab("");
                return;
              }
              if (typeof categoryId === "string") {
                setCategoryToRemove(categoryId);
              }
            },
          }}
        />
      ))}

      <Dialog
        open={props.showAdd}
        maxWidth="lg"
        fullWidth
        onClose={() => {
          props.setShowAdd(false);
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Tabs
            value={optionsTab}
            onChange={(_, value) => setOptionsTab(value)}
          >
            <Tab label="Features" value="features" />
            <Tab label="Components" value="options" />
          </Tabs>
        </DialogTitle>
        <DialogContent sx={{ padding: 6 }}>
          {optionsTab === "features" ? (
            <FeaturesTab
              onAddOption={async (category: CategoryData | CategoryData[]) => {
                if (Array.isArray(category)) {
                  let i = 0;
                  for (const c of category) {
                    i++;
                    await addCategory({
                      variables: { categoryId: c._id },
                    });
                  }

                  productConfigurationQuery.refetch();
                  categoryQuery.refetch();
                  return;
                }
                await addCategory({
                  variables: { categoryId: category._id },
                });
                await productConfigurationQuery.refetch();
                await categoryQuery.refetch();
              }}
              setShowRemove={(categoryId: string) => {}}
            />
          ) : (
            <OptionsTab
              parentProductConfigurationQuery={parentProductConfigurationQuery}
              productConfigurationQuery={productConfigurationQuery}
              categorys={categorys}
              categoryQuery={categoryQuery}
              productConfig={
                (productConfigurationQuery.data?.productConfiguration ??
                  {}) as ProductOptionsData
              }
              onAddOption={async (category: CategoryData) => {
                if (loading) return;
                await addCategory({
                  variables: { categoryId: category._id },
                });
                await productConfigurationQuery.refetch();
                await categoryQuery.refetch();
              }}
              loading={loading}
              catmap={catmap}
              parentCategoriesIds={parentCategoriesIds}
              selectedCategories={selectedCategories}
              setShowRemove={(categoryId: string) => {
                setCategoryToRemove(categoryId);
              }}
              setActiveLoadingCategory={setActiveLoadingCategory}
              activeLoadingCategory={activeLoadingCategory}
              shapeData={shapeData}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => props.setShowAdd(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {productConfigurationQuery?.data?.productConfiguration &&
        categoryToRemove && (
          <RemoveCategory
            open={Boolean(categoryToRemove)}
            productConfigurationId={
              productConfigurationQuery.data.productConfiguration!._id ?? ''
            }
            removeCategoryId={categoryToRemove}
            groupedOptions={groupedOptions}
            onRemoved={() => {
              // ProductConfiguration is un-normalized in cache, so the mutation
              // response does not update the productConfiguration query
              // automatically — refetch to reflect the removed component.
              productConfigurationQuery.refetch();
              props.onOptionsChange?.();
            }}
            onClose={() => {
              setCategoryToRemove(null);
            }}
          />
        )}
    </ExtendedProductConfigurationVariablesProvider>
  );
};
